import zionDebug from '@fs/zion-debug'
import { captureException, setExtras } from '@sentry/react'

const debug = zionDebug('zion:adobe-target:adobeTargetService')

/**
 * Creates a promise for fetching data for a single mbox from Adobe Target
 */
export function createTargetOfferPromise(activity, targetActivityResults) {
  const { location, experienceIds } = activity

  // Skip if already fetched
  if (targetActivityResults[location]) {
    return Promise.resolve(targetActivityResults[location])
  }

  return new Promise((resolve, reject) => {
    window?.adobe?.target?.getOffer({
      mbox: location,
      params: {},
      responseTokens: true, // Request responseTokens
      success(response) {
        debug('at.success.response', location, JSON.stringify(response, null, 2))
        let experience = response[0]?.content?.[0]

        /**
         * The 'experienceIds' array filters Adobe Target responses to only include experiences
         * the application is designed to handle. This filtering:
         * 1. Ensures the app only processes known/supported experiences
         * 2. Provides better control and predictability over which experiments run
         * Without this filtering, unexpected behavior could occur if Adobe Target
         * returns experiences the app doesn't know how to handle.
         *
         */
        if (Array.isArray(experienceIds) && experienceIds.length > 0) {
          const offer = response?.find((item) =>
            item.content?.some((subItem) => experienceIds.includes(subItem.experienceId))
          )
          experience = offer?.content?.find((subItem) => experienceIds.includes(subItem.experienceId))
          if (!experience) {
            console.warn(`No matching experience found for mbox: ${location} with experienceIds: ${experienceIds}`)
            setExtras({ status: 'no_experience_found', location, experienceIds })
            // eslint-disable-next-line prefer-promise-reject-errors
            reject({ location, experienceIds, status: 'no_experience_found' })
          }
        }

        const targetExperience = { location, ...experience }
        debug('targetExperience', targetExperience)

        resolve(targetExperience)
      },
      experienceCloud: {
        analytics: {
          logging: 'server_side',
        },
      },
      error(errorStatus, error) {
        console.error('getOffer error: ', error, errorStatus)
        setExtras({ status: errorStatus, location })
        captureException(error)
        // eslint-disable-next-line prefer-promise-reject-errors
        reject({ location, experienceIds, status: 'error' })
      },
    })
  })
}

/**
 * Fetches Adobe Target offers for all activities and returns promises
 */
export function fetchTargetMboxOffers(adobeTargetStatus, targetActivities, targetActivityResults) {
  // Skip if not needed or already processed
  if (adobeTargetStatus !== 'loaded' || targetActivities.length === 0) {
    return []
  }
  const newOffers = targetActivities.filter((activity) => !targetActivityResults[activity.location])
  return newOffers.map((activity) => createTargetOfferPromise(activity, targetActivityResults))
}

/**
 * Determines if Adobe Target should be skipped based on browser conditions
 */
export function shouldSkipAdobeTarget(isBot, isSafari) {
  return isBot || isSafari
}
